<template>
	<v-container
		fluid
		fill-height
		style="align-items: unset; padding-bottom: 66px;"
		:class="{ 'px-0 pt-2': $vuetify.breakpoint.xs }"
	>
		<AgendaToolbar/>
		<LegendaDaAgenda v-model="mostrarLegenda" />
		<DialogoEvento
			v-if="selecionado"
			v-model="mostraEvento"
			:positionY="positionY"
			:positionX="positionX"
			:evento="selecionado"
			:activator="element"
			:close-on-content-click="false"
			offset-x
			@exclusao="removerEvento"
		/>
		<ComponentAuthGuard :requires="permissoesAdicionarEvento">
			<v-speed-dial
				fixed
				bottom
				right
				:style="$vuetify.breakpoint.mdAndDown
					? { bottom: `${$vuetify.application.bottom + 10}px` }
					: {}
				"
			>
				<template #activator>
					<v-btn
						dark
						fab
						color="primary"
					>
						<v-icon> mdi-plus </v-icon>
					</v-btn>
				</template>
				<v-tooltip left>
					<template #activator="{ on, attrs }">
						<v-btn
							fab
							small
							dark
							
							color="green darken-2"
							:to="{ name: 'novo-evento' }"
          					v-bind="attrs"
							v-on="on"
						>
							<v-icon>mdi-calendar-plus</v-icon>
						</v-btn>
					</template>
					Novo evento
				</v-tooltip>
				<v-tooltip left>
					<template #activator="{ on, attrs }">
						<v-btn
							fab
							small
							dark
							:to="{ name: 'importar-eventos' }"
							color="orange darken-2"
          					v-bind="attrs"
							v-on="on"
						>
							<v-icon>mdi-tray-arrow-down</v-icon>
						</v-btn>
					</template>
					Importar eventos
				</v-tooltip>
			</v-speed-dial>
		</ComponentAuthGuard>

		<v-dialog
			width="300"
			height="420"
			:value="!!dataSelecionarMais"
			@input="() => dataSelecionarMais = null"
			scrollable
		>
			<v-card>
				<v-card-title class="justify-center"> {{ formatarData(dataSelecionarMais || '') }} </v-card-title>
				<v-card-text class="py-0">
					<div v-if="!eventosSelecao.length" class="ma-2 text-center font-italic grey--text">
						Nenhum evento...
					</div>
					<div
						v-for="evento in eventosSelecao"
						:key="evento._id"
						:class="['ma-2 pl-1 white--text', obterCorDoEvento(agenda.tipoDaLabel)(evento)]"
						style="border-radius: 4px;"
						@click="() => selecionarEvento({ event: evento })"
					>
						{{ obterDescricaoDoEvento(agenda.tipoDaLabel)(evento) }}
					</div>
					<ComponentAuthGuard :requires="permissoesAdicionarEvento">
						<div class="d-flex ma-2">
							<v-btn
								block
								text
								color="primary"
								small
								outlined
								@click="() => $router.push({
									name: 'novo-evento',
									query: { data: dataSelecionarMais },
								})"
							>
								<v-icon left> mdi-plus </v-icon>
								Criar Evento
							</v-btn>
						</div>
					</ComponentAuthGuard>
				</v-card-text>

				<v-card-actions>
					<v-btn
						block text color="primary" @click="dataSelecionarMais = null"
					>
						OK
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-card
			min-height="606"
			class="d-flex flex-column"
			style="flex: 1;	"
			v-touch="{
				left: () => calendar.next(),
				right: () => calendar.prev(),
			}"
		>
			<v-calendar
				:events="eventosFiltrados"
				style="height: unset; flex: 1; overflow: hidden;"
				ref="calendar"
				event-start="data"
				:event-color="obterCorDoEvento(agenda.tipoDaLabel)"
				:event-name="(event) => obterDescricaoDoEvento(agenda.tipoDaLabel)(event.input, $vuetify.breakpoint.xs)"
				v-model="focus"
				@click:event="selecionarEvento"
				@click:more="selecionarMais"
				@click:date="selecionarMais"
				@change="updateRange"
				min-weeks="6"
			/>
		</v-card>
	</v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import ComponentAuthGuard from '@/components/ComponentAuthGuard.vue'
import EventoService from '../../services/EventoService'
import { IEvento } from 'common/types/Evento'
import DialogoEvento from '@/components/eventos/DialogoEvento.vue'
import { Loja } from 'common/types/Loja'
import LegendaDaAgenda from '@/components/LegendaDaAgenda.vue'
import { obterCorDoEvento, obterDescricaoDoEvento, PERMISSOES_ADICIONAR_EVENTO, sessaoPorId } from '@/shareds/sharedsEvento'
import { agenda } from '@/store'
import { formatarData } from '../../shareds/formatadores'
import { Touch as vTouch } from 'vuetify/lib/directives'
import AgendaToolbar from './AgendaToolbar.vue'
import { useQueries, useQueryClient } from '@tanstack/vue-query'
import moment from 'moment'
import { extrairMesEAno } from '@/shareds'
import { VCalendarType } from '@/modules/AgendaModule'

const start = ref(moment().toISOString(true))
const end = ref<any | null>(null)

const previousMonth = computed(() => {
	return extrairMesEAno(moment(start.value).subtract(1, 'month').toISOString(true))
})
const currentMonth = computed(() => {
	return extrairMesEAno(start.value)
})
const nextMonth = computed(() => {
	return extrairMesEAno(moment(start.value).add(1, 'month').toISOString(true))
})

const query = useQueries({
	queries: [previousMonth, currentMonth, nextMonth].map(month => ({
		queryKey: ['eventos', month],
		queryFn: () => EventoService.find({
			month: month.value,
		}),
		staleTime: 1000 * 60 * 5, // 5 minutos
	})),
	combine(results) {
		return {
			data: results.flatMap((result) => result.data || []),
			pending: results.some((result) => result.isPending),
		}
	},
})

const queryClient = useQueryClient()

const calendar = ref<VCalendarType>()

const mostrarLegenda = ref(false)

const permissoesAdicionarEvento = PERMISSOES_ADICIONAR_EVENTO

const focus = ref(null)

const selecionado = ref<IEvento | null>(null)
const element = ref<HTMLElement | null>(null)

const mostraEvento = ref(false)

const dataSelecionarMais = ref<string | null>('')

const positionY = ref<number | null>(null)
const positionX = ref<number | null>(null)

const filtro = computed(() => agenda.filter)

function pontuacaoDeOrdenacaoDoEvento(evento: IEvento) {
	switch (evento.tipoDaSessao.nome.toUpperCase()) {
		case 'SOMAR': return 30
		case 'EVENTO': return 29
	}
	return 0
}

const eventosOrdenados = computed(() => {
	return [ ...query.value.data ].sort((eventoA, eventoB) => {
		const [ pontuacaoA, pontuacaoB ] = [ eventoA, eventoB ].map(pontuacaoDeOrdenacaoDoEvento)
		const [ tipoDaSessaoA, tipoDaSessaoB ] = [eventoA, eventoB].map(evento => sessaoPorId[evento.tipoDaSessao.nome] || evento.tipoDaSessao)
		if (pontuacaoA || pontuacaoB)
			return pontuacaoB - pontuacaoA
		if (tipoDaSessaoA.tipoDaEntidade === 'somar' || tipoDaSessaoB.tipoDaEntidade === 'somar')
			return Number(tipoDaSessaoB.tipoDaEntidade === 'somar') - Number(tipoDaSessaoA.tipoDaEntidade === 'somar')
		if (tipoDaSessaoA.tipoDaEntidade === 'potencia' || tipoDaSessaoB.tipoDaEntidade === 'potencia')
			return Number(tipoDaSessaoB.tipoDaEntidade === 'potencia') - Number(tipoDaSessaoA.tipoDaEntidade === 'potencia')
		return eventoA.entidade.nome.localeCompare(eventoB.entidade.nome)
	})
})

const eventosSelecao = computed(() => eventosOrdenados.value.filter(({ data }) => data === dataSelecionarMais.value))

const eventosFiltrados = computed(() => {
	return eventosOrdenados.value
		.filter((evento) => {
			if (filtro.value.loja && evento.entidade._id !== filtro.value.loja.value)
				return false
			if (
				filtro.value.potencia &&
				evento.entidade._id !== filtro.value.potencia.value &&
				(evento.entidade as Omit<Loja, 'potencia'> & { potencia?: string }).potencia !== filtro.value.potencia.value
			)
				return false
			if (filtro.value.grau && evento.grauDaSessao !== filtro.value.grau)
				return false
			if (
				filtro.value.tipoDaSessao &&
				!evento.tipoDaSessao.nome.toUpperCase().includes(filtro.value.tipoDaSessao.toUpperCase())
			)
				return false
			return true
		})
})

function updateRange({ start, end }: { start: any, end: any }) {
	agenda.setInterval({ start, end })
}

function selecionarEvento({ event, nativeEvent  }: { event: IEvento, nativeEvent?: any }) {
	dataSelecionarMais.value = ''
	const open = () => {
		selecionado.value = event
		element.value = nativeEvent?.target || null
		positionY.value = nativeEvent ? null : window.innerHeight / 2 - 180 / 2
		positionX.value = nativeEvent ? null : window.innerWidth / 2 - 256 / 2
		setTimeout(() => mostraEvento.value = true, 10)
	}

	if (mostraEvento.value) {
		mostraEvento.value = false
		setTimeout(open, 10)
	} else {
		open()
	}

	nativeEvent?.stopPropagation()
}

onMounted(() => {
	calendar.value!.checkChange()
	agenda.setCalendar({
		...calendar.value!,
		toToday: () => { focus.value = null },
	})
})

function removerEvento(evento: IEvento) {
	queryClient.invalidateQueries({
		queryKey: ['eventos', extrairMesEAno(evento.data)],
		refetchType: 'all',
	})
}

function selecionarMais(evento: any) {
	dataSelecionarMais.value = evento.date
}

watch(() => agenda.start, (novoStart: any) => {
	start.value = novoStart.date
})

watch(() => agenda.end, (novoEnd: any) => {
	end.value = novoEnd.date
})
</script>

<style>
.v-calendar  .v-event {
	border-radius: 2px;
	margin-left: 2px;
	margin-right: 2px;
	width: unset !important;
}

@media only screen and (max-width: 599px) {
	.v-calendar .v-event {
		padding-top: 2px;
		font-size: 10px;
		line-height: 12px;
		height: unset !important;
	}
}

.v-present > .v-calendar-weekly__day-label > button {
	border: 2px solid var(--v-primary-base) !important;
	background: transparent !important;
	color: var(--v-primary-base) !important;
	font-weight: bold;
}

.v-calendar-weekly__day-label {
	font-size: 0;
	margin-bottom: 2px;
	display: flex;
}

.v-calendar-weekly__day-label > button {
	height: 18px !important;
	border-radius: 2px !important;
	flex: 1;
	margin: 0 2px;
}

.v-calendar-weekly,
.v-calendar-weekly__head-weekday,
.v-calendar-weekly__day {
	border-color: #BBB !important;
}

</style>
